import { useRef, useCallback } from 'react';
import { useBuilderStore } from '../store/builderStore';
import { useBuilderState } from './useBuilderState';
import { useFabricCanvas } from './useFabricCanvas';
import { loadGoogleFont, loadAllWeights } from '../lib/fonts';
import { Path, IText, Textbox } from 'fabric';
import { CurvedIText } from '../utils/CurvedIText';
import { buildCurvePath, getCurveVisualBounds, getAutoPathStartOffset } from '../utils/curveUtils';
import type { CurvePreset } from '../utils/curveUtils';
import type { FabricObject, Group } from 'fabric';
import { isBuilderDebug } from '../lib/debugFlags';
import type { DesignNode, ShapeNodeData, TextNodeData } from '../types/node.types';

export const useBuilderNode = () => {
  const {
    selectedNodeId,
    surfaces,
    currentSurface,
    updateNode,
    deleteNode: storeDeleteNode,
    setSelectedNodeId,
  } = useBuilderState();

  const { canvasRef } = useFabricCanvas();

  const selectedNode = selectedNodeId
    ? surfaces[currentSurface]?.find(node => node.id === selectedNodeId)
    : null;

  const getFabricObject = (nodeId: string): FabricObject | null =>
    canvasRef.current?.getObjects().find((o) => (o as FabricObject & { id?: string }).id === nodeId) ?? null;

  const selectNode = (nodeId: string | null) => {
    setSelectedNodeId(nodeId);
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!nodeId) {
      canvas.discardActiveObject();
      canvas.renderAll();
      return;
    }
    const obj = getFabricObject(nodeId);
    if (obj && canvas.getActiveObject() !== obj) {
      canvas.setActiveObject(obj);
      canvas.renderAll();
    }
  };

  const updateNodePosition = (nodeId: string, position: { x: number; y: number }) => {
    updateNode(nodeId, { position });
    const obj = getFabricObject(nodeId);
    if (obj) {
      obj.set({ left: position.x, top: position.y });
      obj.setCoords();
      canvasRef.current?.renderAll();
    }
  };

  const updateNodeSize = (nodeId: string, size: { width: number; height: number }) => {
    updateNode(nodeId, { size });
    const obj = getFabricObject(nodeId);
    if (obj) {
      // For Textbox, only set width — height is computed from text content.
      // Setting height on a Textbox is ignored by Fabric and causes stale values.
      if (obj.type === 'textbox') {
        obj.set({ width: size.width, scaleX: 1, scaleY: 1 });
        (obj as unknown as Textbox).initDimensions();
      } else {
        obj.set({ width: size.width, height: size.height, scaleX: 1, scaleY: 1 });
      }
      obj.setCoords();
      canvasRef.current?.renderAll();
    }
  };

  const updateNodeRotation = (nodeId: string, rotation: number) => {
    updateNode(nodeId, { rotation });
    const obj = getFabricObject(nodeId);
    if (obj) {
      obj.set({ angle: rotation });
      obj.setCoords();
      canvasRef.current?.renderAll();
    }
  };

  const updateNodeOpacity = (nodeId: string, opacity: number) => {
    updateNode(nodeId, { opacity });
    const obj = getFabricObject(nodeId);
    if (obj) {
      obj.set({ opacity });
      canvasRef.current?.renderAll();
    }
  };

  const toggleNodeVisibility = (nodeId: string) => {
    const node = surfaces[currentSurface]?.find(n => n.id === nodeId);
    if (!node) return;
    const newVisible = !node.visible;
    updateNode(nodeId, { visible: newVisible });
    const obj = getFabricObject(nodeId);
    if (obj) {
      obj.set({ visible: newVisible, evented: newVisible });
      canvasRef.current?.renderAll();
    }
  };

  const bringToFront = (nodeId: string) => {
    const currentSurfaceNodes = surfaces[currentSurface] || [];
    if (!currentSurfaceNodes.length) return;
    const maxZIndex = Math.max(...currentSurfaceNodes.map(n => n.zIndex));
    updateNode(nodeId, { zIndex: maxZIndex + 1 });
    const obj = getFabricObject(nodeId);
    if (obj) {
      canvasRef.current?.bringObjectToFront(obj);
      canvasRef.current?.renderAll();
    }
  };

  const sendToBack = (nodeId: string) => {
    const currentSurfaceNodes = surfaces[currentSurface] || [];
    if (!currentSurfaceNodes.length) return;
    const minZIndex = Math.min(...currentSurfaceNodes.map(n => n.zIndex));
    updateNode(nodeId, { zIndex: minZIndex - 1 });
    const obj = getFabricObject(nodeId);
    if (obj) {
      canvasRef.current?.sendObjectToBack(obj);
      canvasRef.current?.renderAll();
    }
  };

  const deleteNode = (nodeId: string) => {
    storeDeleteNode(nodeId);
    const obj = getFabricObject(nodeId);
    if (obj) {
      canvasRef.current?.remove(obj);
      canvasRef.current?.renderAll();
    }
  };

  // ── Fill color updates (debounced store, immediate canvas) ──────────────

  const fillDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const updateNodeFill = useCallback((nodeId: string, color: string) => {
    const node = surfaces[currentSurface]?.find((n: DesignNode) => n.id === nodeId);
    if (!node) return;

    // Immediate visual update on Fabric canvas
    const obj = getFabricObject(nodeId);
    if (obj) {
      if (node.type === 'svg') {
        // SVG group: iterate sub-objects, recolor non-transparent fills
        const group = obj as unknown as Group;
        group.getObjects().forEach((child: FabricObject) => {
          const currentFill = (child as FabricObject & { fill?: string }).fill;
          if (currentFill && currentFill !== 'none' && currentFill !== 'transparent') {
            child.set({ fill: color });
          }
        });
      } else {
        obj.set({ fill: color });
      }
      canvasRef.current?.renderAll();
    }

    // Debounced store update to avoid flooding undo history
    clearTimeout(fillDebounceRef.current);
    fillDebounceRef.current = setTimeout(() => {
      updateNode(nodeId, { data: { ...node.data, fill: color } });
    }, 300);
  }, [surfaces, currentSurface, updateNode]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateNodeStroke = useCallback((nodeId: string, color: string) => {
    const node = surfaces[currentSurface]?.find((n: DesignNode) => n.id === nodeId);
    if (!node || node.type !== 'shape') return;

    const obj = getFabricObject(nodeId);
    if (obj) {
      obj.set({ stroke: color });
      canvasRef.current?.renderAll();
    }

    clearTimeout(fillDebounceRef.current);
    fillDebounceRef.current = setTimeout(() => {
      updateNode(nodeId, { data: { ...(node.data as ShapeNodeData), stroke: color } });
    }, 300);
  }, [surfaces, currentSurface, updateNode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Text property updates ──────────────────────────────────────────────

  const updateNodeTextProp = useCallback(async (
    nodeId: string,
    prop: keyof TextNodeData,
    value: string | number | boolean | undefined
  ) => {
    const node = surfaces[currentSurface]?.find((n: DesignNode) => n.id === nodeId);
    if (!node || node.type !== 'text') return;

    const obj = getFabricObject(nodeId);
    const textData = node.data as TextNodeData;
    const needsFontLoad = prop === 'fontFamily' || prop === 'fontWeight';

    // Dim the text object while the font binary downloads
    if (needsFontLoad && obj) {
      obj.set({ opacity: 0.4 });
      canvasRef.current?.renderAll();
    }

    // Load fonts: all weights for family change, specific weight for weight change
    if (prop === 'fontFamily' && typeof value === 'string') {
      await loadAllWeights(value);
    } else if (prop === 'fontWeight' && typeof value === 'number') {
      await loadGoogleFont(textData.fontFamily, value);
    }

    const updatedData = { ...textData, [prop]: value };

    // Mutual exclusion: turning one case on clears the other
    if (prop === 'uppercase' && value) updatedData.lowercase = false;
    if (prop === 'lowercase' && value) updatedData.uppercase = false;

    if (obj) {
      if (prop === 'uppercase' || prop === 'lowercase') {
        // Fabric.js has no textTransform — apply case visually on the canvas object
        const displayText = updatedData.uppercase
          ? updatedData.text.toUpperCase()
          : updatedData.lowercase
            ? updatedData.text.toLowerCase()
            : updatedData.text;
        obj.set({ text: displayText });
      } else if (prop === 'text') {
        // When text changes, respect current case setting
        const raw = String(value);
        const displayText = updatedData.uppercase ? raw.toUpperCase() : updatedData.lowercase ? raw.toLowerCase() : raw;
        obj.set({ text: displayText });
      } else {
        obj.set({ [prop]: value });
      }
      // Recalculate char widths after font/size/spacing changes so cursor lands correctly
      if (prop === 'fontFamily' || prop === 'fontSize' || prop === 'fontWeight' || prop === 'charSpacing') {
        (obj as FabricObject & { initDimensions?: () => void }).initDimensions?.();
      }
      // Restore original opacity after font load
      if (needsFontLoad) {
        obj.set({ opacity: node.opacity });
      }
      canvasRef.current?.renderAll();

      // After font-related changes on straight text, re-measure width and update store size
      // so the Textbox width matches the new content size (curved text has its own sizing)
      if ((prop === 'fontFamily' || prop === 'fontSize' || prop === 'fontWeight' || prop === 'charSpacing') && !textData.curvePathData) {
        const tbox = obj as unknown as Textbox;
        // Temporarily widen to prevent wrapping, then read Fabric's own measurement
        tbox.set({ width: 10000 });
        tbox.initDimensions();
        const measured = tbox.calcTextWidth();
        const correctedWidth = Math.max(20, Math.ceil(measured) + 4);
        tbox.set({ width: correctedWidth });
        tbox.initDimensions();
        tbox.setCoords();
        canvasRef.current?.renderAll();
        updateNode(nodeId, {
          data: updatedData,
          size: { width: correctedWidth, height: tbox.height ?? node.size.height },
        });
        return;
      }
    }

    updateNode(nodeId, { data: updatedData });
  }, [surfaces, currentSurface, updateNode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fit text width to content ─────────────────────────────────────────

  const fitTextToContent = useCallback((nodeId: string) => {
    const node = surfaces[currentSurface]?.find((n: DesignNode) => n.id === nodeId);
    if (!node || node.type !== 'text') return;

    const obj = getFabricObject(nodeId) as (Textbox & { id?: string }) | null;
    if (!obj) return;

    const textObj = obj as unknown as IText;
    const text = textObj.text ?? '';
    const data = node.data as TextNodeData;
    const preset = (data.curvePreset ?? 'none') as CurvePreset;

    // Measure text width using Fabric's own engine: temporarily set a very wide
    // box so no wrapping occurs, then read calcTextWidth for the true single-line width.
    // This is more reliable than canvas2d.measureText which can differ from Fabric's
    // internal metrics (kerning, charSpacing, rounding).
    const savedWidth = obj.width;
    obj.set({ width: 10000 });
    obj.initDimensions();
    const textWidth = obj.calcTextWidth();
    obj.set({ width: savedWidth }); // restore in case we bail early

    const newWidth = Math.max(20, Math.ceil(textWidth) + 4);

    if (preset !== 'none') {
      // For curved text: rebuild the path with the measured width, recalc padding
      const curveAmount = data.curveAmount ?? 50;
      const curveRadius = preset === 'circle' ? (data.curveRadius ?? Math.round(newWidth / 2)) : data.curveRadius;
      const pathData = buildCurvePath(preset, newWidth, curveAmount, curveRadius);

      if (pathData) {
        const debug = isBuilderDebug();
        const curvePath = new Path(pathData, { visible: debug, selectable: false, evented: false, stroke: debug ? 'red' : '', fill: 'transparent' });
        const autoOffset = getAutoPathStartOffset(
          preset, text,
          textObj.fontSize ?? 40, textObj.fontFamily ?? 'Arial',
          textObj.fontWeight ?? 400, textObj.fontStyle ?? 'normal',
          textObj.charSpacing ?? 0, newWidth, curveAmount, curveRadius,
        );
        textObj.set({ path: curvePath, pathStartOffset: autoOffset, pathSide: 'left', pathAlign: 'center' } as Partial<IText>);
      }

      obj.initDimensions();
      const fs = textObj.fontSize ?? 40;
      const bounds = getCurveVisualBounds(preset, newWidth, fs, data.curveAmount ?? 50, curveRadius);
      const padX = Math.max(0, (bounds.width - (obj.width ?? 0)) / 2);
      const padY = Math.max(0, (bounds.height - (obj.height ?? 0)) / 2);
      obj.set({ padding: Math.ceil(Math.max(padX, padY)) });
      obj.setCoords();
      canvasRef.current?.renderAll();

      updateNode(nodeId, {
        size: { width: bounds.width, height: bounds.height },
        data: { ...data, curvePathData: pathData, curveRadius },
      });
    } else {
      obj.set({ width: newWidth });
      obj.initDimensions();
      obj.setCoords();
      canvasRef.current?.renderAll();

      updateNode(nodeId, {
        size: { width: newWidth, height: obj.height ?? node.size.height },
      });
    }
  }, [surfaces, currentSurface, updateNode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Curve text updates (swap IText ↔ Textbox) ──────────────────────────

  const curveDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const updateNodeCurve = useCallback((
    nodeId: string,
    curvePathData: string | undefined,
    curveAmount: number,
    curveRadius?: number,
    curvePreset?: CurvePreset,
    pathStartOffset?: number,
  ) => {
    const node = surfaces[currentSurface]?.find((n: DesignNode) => n.id === nodeId);
    if (!node || node.type !== 'text') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const data = node.data as TextNodeData;
    const oldObj = getFabricObject(nodeId);

    // Extract visual properties from the existing Fabric text object
    const textContent = oldObj
      ? (oldObj as unknown as IText).text ?? data.text
      : data.text;
    const displayText = data.uppercase ? textContent.toUpperCase() : data.lowercase ? textContent.toLowerCase() : textContent;

    const props = oldObj ? {
      left: oldObj.left, top: oldObj.top, angle: oldObj.angle,
      opacity: oldObj.opacity, visible: oldObj.visible, evented: oldObj.evented,
      fontSize: (oldObj as unknown as IText).fontSize,
      fontFamily: (oldObj as unknown as IText).fontFamily,
      fontWeight: (oldObj as unknown as IText).fontWeight,
      fontStyle: (oldObj as unknown as IText).fontStyle,
      fill: (oldObj as unknown as IText).fill,
      textAlign: (oldObj as unknown as IText).textAlign,
      underline: (oldObj as unknown as IText).underline,
      charSpacing: (oldObj as unknown as IText).charSpacing,
      lineHeight: (oldObj as unknown as IText).lineHeight,
    } : {
      left: node.position.x, top: node.position.y, angle: node.rotation,
      opacity: node.opacity, visible: node.visible, evented: node.visible,
      fontSize: data.fontSize, fontFamily: data.fontFamily,
      fontWeight: data.fontWeight ?? 400, fontStyle: data.fontStyle ?? 'normal',
      fill: data.fill, textAlign: data.textAlign ?? 'left',
      underline: data.underline ?? false, charSpacing: data.charSpacing ?? 0,
      lineHeight: data.lineHeight ?? 1.16,
    };

    // Remove old object
    if (oldObj) canvas.remove(oldObj);

    // Create new object: CurvedIText for curved, Textbox for straight
    let newObj: CurvedIText | Textbox;
    if (curvePathData) {
      const debug = isBuilderDebug();
      const curvePath = new Path(curvePathData, { visible: debug, selectable: false, evented: false, stroke: debug ? 'red' : '', fill: 'transparent' });
      const pathSide = 'left';
      const autoOffset = pathStartOffset != null && pathStartOffset !== 0 ? pathStartOffset : getAutoPathStartOffset(
        (curvePreset ?? 'none') as CurvePreset,
        displayText,
        props.fontSize ?? 40,
        props.fontFamily ?? 'Arial',
        props.fontWeight ?? 400,
        props.fontStyle ?? 'normal',
        props.charSpacing ?? 0,
        node.size.width,
        curveAmount,
        curveRadius,
      );
      newObj = new CurvedIText(displayText, { ...props, path: curvePath, pathStartOffset: autoOffset, pathSide, pathAlign: 'center', objectCaching: false });
      // Compute exact padding from curve geometry so bounding box matches rendered text
      newObj.initDimensions();
      const fs = props.fontSize ?? 40;
      const textW = newObj.width || node.size.width;
      const bounds = getCurveVisualBounds((curvePreset ?? 'none') as CurvePreset, textW, fs, curveAmount, curveRadius);
      const padX = Math.max(0, (bounds.width - newObj.width) / 2);
      const padY = Math.max(0, (bounds.height - newObj.height) / 2);
      newObj.set({ padding: Math.ceil(Math.max(padX, padY)) });
      newObj.setCoords();
    } else {
      newObj = new Textbox(displayText, {
        ...props,
        width: node.size.width,
        breakWords: data.breakWords ?? true,
      });
    }

    (newObj as FabricObject & { id?: string }).id = nodeId;

    // Save text to store when editing finishes (path stays during editing for cursor accuracy)
    newObj.on('editing:exited', () => {
      const { updateNode: storeUpdate } = useBuilderStore.getState();
      const currentNode = useBuilderStore.getState().surfaces[
        useBuilderStore.getState().currentSurface
      ]?.find((n: DesignNode) => n.id === nodeId);
      if (currentNode) {
        storeUpdate(nodeId, { data: { ...currentNode.data, text: newObj.text ?? '' } });
      }
    });

    canvas.add(newObj);
    canvas.setActiveObject(newObj);
    canvas.renderAll();

    // Compute updated size from visual bounds
    const updatedSize = curvePathData && curvePreset && curvePreset !== 'none'
      ? getCurveVisualBounds(
          curvePreset as CurvePreset,
          newObj.width || node.size.width,
          props.fontSize ?? 40,
          curveAmount,
          curveRadius,
        )
      : { width: newObj.width || node.size.width, height: newObj.height || node.size.height };

    // Debounced store update
    clearTimeout(curveDebounceRef.current);
    curveDebounceRef.current = setTimeout(() => {
      updateNode(nodeId, {
        size: { width: updatedSize.width, height: updatedSize.height },
        data: { ...(node.data as TextNodeData), curvePathData, curveAmount, curveRadius, curvePreset, pathStartOffset },
      });
    }, 300);
  }, [surfaces, currentSurface, updateNode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debug: live text metrics ───────────────────────────────────────────

  const getTextDebugInfo = useCallback((nodeId: string) => {
    if (!isBuilderDebug()) return null;
    const node = surfaces[currentSurface]?.find((n: DesignNode) => n.id === nodeId);
    if (!node || node.type !== 'text') return null;

    const obj = getFabricObject(nodeId) as (Textbox & { id?: string }) | null;
    if (!obj) return null;

    // Measure rendered width directly via a temp canvas at the actual font size
    const textObj = obj as unknown as IText;
    const text = textObj.text ?? '';
    const canvas2d = document.createElement('canvas').getContext('2d');
    let directMeasure = 0;
    if (canvas2d) {
      canvas2d.font = `${textObj.fontStyle ?? 'normal'} ${textObj.fontWeight ?? 400} ${textObj.fontSize}px ${textObj.fontFamily}`;
      directMeasure = canvas2d.measureText(text).width;
    }

    // Compute curve visual bounds if a curve preset is active
    const textData = node.data as TextNodeData;
    const curvePreset = (textData.curvePreset ?? 'none') as CurvePreset;
    const curveVisual = curvePreset !== 'none'
      ? getCurveVisualBounds(
          curvePreset,
          directMeasure,
          textObj.fontSize ?? 40,
          textData.curveAmount ?? 50,
          textData.curveRadius,
        )
      : null;

    return {
      textWidth: obj.calcTextWidth(),
      directMeasure,
      boundingBoxWidth: obj.width,
      layerWidth: node.size.width,
      fabricHeight: obj.height,
      layerHeight: node.size.height,
      scaleX: obj.scaleX,
      scaleY: obj.scaleY,
      fontSize: textObj.fontSize,
      charSpacing: textObj.charSpacing,
      textLines: (obj as unknown as Record<string, unknown[]>)._textLines?.length ?? 0,
      curvePreset,
      curveVisualWidth: curveVisual?.width ?? null,
      curveVisualHeight: curveVisual?.height ?? null,
      curveRadius: textData.curveRadius ?? null,
    };
  }, [surfaces, currentSurface]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    selectedNode,
    selectNode,
    updateNodePosition,
    updateNodeSize,
    updateNodeRotation,
    updateNodeOpacity,
    toggleNodeVisibility,
    bringToFront,
    sendToBack,
    deleteNode,
    updateNodeFill,
    updateNodeStroke,
    updateNodeTextProp,
    fitTextToContent,
    updateNodeCurve,
    getTextDebugInfo,
  };
};
